import { create } from 'zustand';
import { Session, ConsentAgreement } from '../types';
import { supabase } from '../services/supabase';

interface SessionState {
  session: Session | null;
  createSession: (userId: string) => Promise<string | null>;
  loadSession: (sessionId: string) => Promise<void>;
  updateSession: (session: Session) => void;
  setConsent: (consent: ConsentAgreement) => void;
  endSession: () => void;
  joinSession: (sessionId: string, userId: string, role: 'A' | 'B') => Promise<boolean>;
  updateGameState: (gameState: any) => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  session: null,

  createSession: async (userId: string) => {
    const { data, error } = await supabase
      .from('sessions')
      .insert({ created_by: userId })
      .select()
      .single();

    if (error || !data) {
      console.error('Failed to create session:', error);
      return null;
    }

    const session = mapDbSession(data);
    set({ session });
    return session.id;
  },

  loadSession: async (sessionId: string) => {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (error || !data) {
      console.error('Failed to load session:', error);
      return;
    }

    set({ session: mapDbSession(data) });
  },

  joinSession: async (sessionId: string, userId: string, role: 'A' | 'B') => {
    const { data: existing } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (!existing) return false;

    const updateField = role === 'A' ? 'partner_a' : 'partner_b';
    const partnerField = role === 'A' ? 'partner_b' : 'partner_a';
    const updates: any = {
      [updateField]: {
        userId,
        connected: true,
        toyConnected: false,
        joinedAt: new Date().toISOString()
      }
    };

    const partner = existing[partnerField];
    if (partner?.userId && partner?.connected) {
      updates.status = 'active';
    }

    const { error } = await supabase
      .from('sessions')
      .update(updates)
      .eq('id', sessionId);

    if (error) {
      console.error('Failed to join session:', error);
      return false;
    }

    const updated = { ...existing, ...updates };
    set({ session: mapDbSession(updated) });
    return true;
  },

  updateSession: (session) => set({ session }),

  setConsent: (consent) => set((state) => {
    if (!state.session) return state;
    const updated = { ...state.session, consent, consentEstablished: true };
    supabase.from('sessions').update({ consent }).eq('id', state.session.id);
    return { session: updated };
  }),

  updateGameState: (gameState) => set((state) => {
    if (!state.session) return state;
    const updated = { ...state.session, gameState };
    supabase.from('sessions').update({ game_state: gameState }).eq('id', state.session.id);
    return { session: updated };
  }),

  endSession: () => set({ session: null })
}));

function mapDbSession(row: any): Session {
  return {
    id: row.id,
    createdBy: row.created_by,
    partnerA: row.partner_a || { userId: '', connected: false, toyConnected: false },
    partnerB: row.partner_b || { userId: '', connected: false, toyConnected: false },
    status: row.status,
    consent: row.consent || {
      established: false, safeWord: '', boundaries: [], maxIntensity: 20,
      maxDenialDuration: 5, checkInInterval: 10
    },
    gameState: row.game_state || { scores: {}, data: {} },
    createdAt: row.created_at,
    endedAt: row.ended_at
  };
}
